// Pack desk: "This is a data storage order" (per Alex, 2026-10-27).
//
// DSD orders (order_id DSD_UK_… / DSD_PL_…) are data devices going out on
// collection, and they must stay in their original packaging. The notice is
// shown INSTEAD of the pack screen the moment one is opened, and the only
// way on is to scan an item of the order — so the packer has read it before
// anything is repacked. The scan is the pack scan: the parent counts it on
// its line (when the line still has a unit to scan) and opens the pack
// screen.
import { useState } from "react";
import { HardDrive, ArrowLeft, ScanBarcode } from "lucide-react";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { scannableBarcodes } from "./itemBarcodes";
import { PackerNotesList, type PackerNote } from "./packer-notes";
import type { DispatchOrder, DispatchOrderItem } from "../../types/Dispatch";

export const isDsdOrder = (order: Pick<DispatchOrder, "order_id">): boolean =>
    /^DSD_/i.test(order.order_id || "");

const resolvedCount = (i: DispatchOrderItem): number =>
    (i.scannedCount || 0) + (i.missingCount || 0) + (i.damagedCount || 0)
    + (i.wrongItemCount || 0) + (i.barcodeDamagedCount || 0);

export const hasUnitLeft = (i: DispatchOrderItem): boolean => resolvedCount(i) < i.quantity;

// The line a notice's scan lands on: one with a unit left when there is one,
// else any line the barcode belongs to. Null = not an item of this order.
export const itemForScan = (order: DispatchOrder, val: string): DispatchOrderItem | null => {
    const matches = order.items.filter(i => i.sku === val || scannableBarcodes(i).includes(val));
    return matches.find(hasUnitLeft) ?? matches[0] ?? null;
};

type Props = {
    order: DispatchOrder;
    /** Order notes for the packer — shown under the instruction so a DSD
     *  order with notes needs one scan, not two notices. */
    notes?: PackerNote[];
    /** An item of the order was scanned. The line is one with a unit left
     *  to scan when there is one; otherwise the unit was already counted
     *  (the identifying scan) and the parent only moves on. */
    onScanned: (item: DispatchOrderItem) => void;
    /** Leave the order without packing it. */
    onLeave?: () => void;
};

const DsdOrderNotice = ({ order, notes, onScanned, onLeave }: Props) => {
    const { t } = useTranslation();
    const [scanValue, setScanValue] = useState("");
    const [shake, setShake] = useState(false);

    const doSubmit = (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        setScanValue("");
        if (!val) return;
        const item = itemForScan(order, val);
        if (!item) {
            playError();
            setShake(true);
            window.setTimeout(() => setShake(false), 400);
            return;
        }
        playSuccess();
        onScanned(item);
    };

    return (
        <div className="dispatch-page page-data fp-notice-page" data-testid="dsd-notice">
            <div className={`fp-notice dsd-notice${shake ? " dsd-notice--shake" : ""}`}>
                <div className="fp-notice-head">
                    <span className="fp-notice-icon dsd-notice-icon">
                        <HardDrive size={20} />
                    </span>
                    <h2>{t("dsd_notice_title")}</h2>
                </div>
                <p className="dsd-notice-instruction">{t("dsd_notice_instruction")}</p>
                {notes && notes.length > 0 && <PackerNotesList notes={notes} />}
                <p className="dsd-notice-scan">
                    <ScanBarcode size={16} />
                    {t("dsd_notice_scan")}
                </p>
                {onLeave && (
                    <div className="fp-notice-actions">
                        <button type="button" className="btn btn-ghost fp-notice-btn" onClick={onLeave} data-testid="dsd-notice-leave">
                            <ArrowLeft size={16} />
                            {t("fp_notice_leave")}
                        </button>
                    </div>
                )}
            </div>
            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
            />
        </div>
    );
};

export default DsdOrderNotice;
